import { FC } from 'react';
import { 
  Building2, 
  TrendingUp, 
  Users, 
  HelpCircle, 
  ArrowUpRight, 
  CheckCircle, 
  AlertTriangle, 
  ShieldCheck, 
  Star,
  Activity,
  QrCode,
  Sparkles,
  Trophy,
  Flame,
  Clock,
  ExternalLink
} from 'lucide-react';

interface DashboardMetrics {
  totalRest: number;
  activeRest: number;
  trialRest: number;
  suspendedRest: number;
  mrr: number;
  totalScans: number;
  totalOrderRevenue: number;
  avgRating: number;
  openTicketsCount: number;
}

interface Restaurant {
  id: string;
  name: string;
  cuisine: string;
  status: 'active' | 'suspended' | 'trial';
  plan: string;
  expiryDate: string;
  scansThisMonth: number;
  rating: number;
  revenueThisMonth: number;
  ownerName: string;
}

interface DashboardViewProps {
  metrics: DashboardMetrics;
  restaurants: Restaurant[];
  setActiveTab: (tab: string) => void;
}

export const DashboardView: FC<DashboardViewProps> = ({ metrics, restaurants, setActiveTab }) => {
  // Find trials expiring soon
  const trialRestaurants = restaurants.filter(r => r.status === 'trial');
  
  // Custom mock analytics data for scan trends (weekly)
  const scanData = [
    { label: 'B.e', value: 3120 },
    { label: 'Ç.a', value: 4340 },
    { label: 'Ç', value: 4900 },
    { label: 'C.a', value: 5800 },
    { label: 'C', value: 9150 },
    { label: 'Ş', value: 11200 },
    { label: 'B', value: 8900 },
  ];

  const maxScanValue = Math.max(...scanData.map(d => d.value));

  // Determine top performing restaurants sorted by scans
  const topRestaurants = [...restaurants]
    .sort((a, b) => b.scansThisMonth - a.scansThisMonth)
    .slice(0, 4);

  // Quick statistics breakdown of active plans
  const planDistribution = restaurants.reduce((acc: Record<string, number>, r) => {
    if (r.status === 'active') {
      acc[r.plan] = (acc[r.plan] || 0) + 1;
    }
    return acc;
  }, {});

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Upper Navigation Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="font-display font-bold text-3xl tracking-tight text-slate-900">
            HQ İdarəetmə Panelinə Xoş Gəlmisiniz!
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Restoran QR-menyularının fəaliyyəti, abunəliklər, ödənişlər və dəstək biletlərinin canlı monitorinqi.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono font-medium rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            CRM Aktivdir
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono font-medium rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">
            <ShieldCheck className="w-3.5 h-3.5" />
            Super-Admin Rejimi
          </span>
        </div>
      </div>

      {/* Grid of 4 High-impact metrics cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        
        {/* Metric 1: MRR with AZN Currency */}
        <div className="bg-white p-6 rounded-xl border border-slate-200/80 shadow-sm flex items-center justify-between hover:shadow-md transition-shadow">
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 font-mono">Platformanın MRR-i</p>
            <h3 className="text-3xl font-display font-extrabold text-slate-900">{metrics.mrr.toLocaleString()} ₼</h3>
            <p className="text-xs text-emerald-600 flex items-center gap-1 font-medium font-mono">
              <TrendingUp className="w-3 h-3" />
              <span>+18.3% / son ay</span>
            </p>
          </div>
          <div className="p-3.5 bg-indigo-50 text-indigo-600 rounded-xl">
            <span className="text-xl font-bold font-mono">₼</span>
          </div>
        </div>

        {/* Metric 2: Restoranlar */}
        <div className="bg-white p-6 rounded-xl border border-slate-200/80 shadow-sm flex items-center justify-between hover:shadow-md transition-shadow">
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 font-mono">Ümumi Restoranlar</p>
            <h3 className="text-3xl font-display font-extrabold text-slate-900">{metrics.totalRest}</h3>
            <div className="flex gap-2 text-[10px] font-mono mt-0.5">
              <span className="text-emerald-700 font-bold">{metrics.activeRest} akt.</span>
              <span className="text-amber-700 font-bold">{metrics.trialRest} sınaq</span>
              <span className="text-slate-500 font-bold">{metrics.suspendedRest} dond.</span>
            </div>
          </div>
          <div className="p-3.5 bg-emerald-50 text-emerald-600 rounded-xl">
            <Building2 className="w-6 h-6" />
          </div>
        </div>

        {/* Metric 3: Skanlar */}
        <div className="bg-white p-6 rounded-xl border border-slate-200/80 shadow-sm flex items-center justify-between hover:shadow-md transition-shadow">
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 font-mono">Aylıq Skan Sayı</p>
            <h3 className="text-3xl font-display font-extrabold text-slate-900">
              {metrics.totalScans.toLocaleString()}
            </h3>
            <p className="text-xs text-slate-500 flex items-center gap-1 font-mono">
              <Activity className="w-3.5 h-3.5 text-indigo-500" />
              <span>~{Math.round(metrics.totalScans / 30).toLocaleString()} skan/gün</span>
            </p>
          </div>
          <div className="p-3.5 bg-purple-50 text-purple-600 rounded-xl">
            <QrCode className="w-6 h-6" />
          </div>
        </div>

        {/* Metric 4: Müştəri Dəstəyi */}
        <div className="bg-white p-6 rounded-xl border border-slate-200/80 shadow-sm flex items-center justify-between hover:shadow-md transition-shadow">
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 font-mono">Açıq Biletlər</p>
            <h3 className="text-3xl font-display font-extrabold text-slate-900">
              {metrics.openTicketsCount}
            </h3>
            <p className={`text-xs flex items-center gap-1 font-medium ${metrics.openTicketsCount > 0 ? 'text-rose-600 font-bold' : 'text-slate-500'}`}>
              {metrics.openTicketsCount > 0 ? (
                <>
                  <AlertTriangle className="w-3 h-3 text-rose-500 animate-bounce" />
                  <span>Cavab yazılmalıdır!</span>
                </>
              ) : (
                <>
                  <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                  <span>Bütün müraciətlər həll olunub</span>
                </>
              )}
            </p>
          </div>
          <div className={`p-3.5 rounded-xl ${metrics.openTicketsCount > 0 ? 'bg-rose-50 text-rose-600' : 'bg-slate-100 text-slate-600'}`}>
            <HelpCircle className="w-6 h-6" />
          </div>
        </div>

      </div>

      {/* Useful Analytics: Skan Qrafiki + Lider Restoranlar Panel */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Left Side Chart of Scan metrics */}
        <div className="bg-white p-6 rounded-2xl border border-slate-200/80 shadow-sm lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-display font-bold text-lg text-slate-900">
                Həftəlik QR Menyu Skan Statistikası
              </h4>
              <p className="text-xs text-slate-400">Son 7 gün ərzində QR kodların oxunma payı</p>
            </div>
            <span className="text-xs font-mono font-bold bg-indigo-50 text-indigo-600 px-2.5 py-1 rounded">
              Cari Həftə
            </span>
          </div>

          {/* SVG Custom Responsive Bar Chart */}
          <div className="h-64 flex items-end justify-between gap-2 pt-6">
            {scanData.map((data, idx) => {
              const heightPercentage = Math.round((data.value / maxScanValue) * 80) + 10;
              return (
                <div key={idx} className="flex-1 flex flex-col items-center group cursor-pointer relative">
                  {/* Tooltip value */}
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-slate-950 text-white text-[10px] font-mono px-2 py-1 rounded mb-2 shadow absolute -translate-y-16 pointer-events-none z-10 whitespace-nowrap">
                    {data.value.toLocaleString()} skan
                  </div>
                  
                  {/* Styled Bar */}
                  <div 
                    style={{ height: `${heightPercentage}%` }}
                    className="w-full bg-gradient-to-t from-indigo-600 to-indigo-400 rounded-t-md group-hover:from-indigo-500 group-hover:to-indigo-300 transition-all duration-300 flex items-end justify-center shadow-sm"
                  >
                    <div className="w-1.5 h-3/4 bg-white/20 rounded-full mb-1 opacity-40 group-hover:opacity-60" />
                  </div>
                  
                  {/* Day Label */}
                  <span className="text-xs font-semibold text-slate-500 font-mono mt-3 uppercase tracking-wider">
                    {data.label}
                  </span>
                </div>
              );
            })}
          </div>

          <div className="flex justify-between items-center text-xs text-slate-400 font-mono border-t border-slate-100 pt-4">
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded bg-indigo-600 inline-block" />
              Sistem Cavab Sürəti: Normal (0.02s)
            </span>
            <span>Maksimum: {maxScanValue.toLocaleString()} skan/gün</span>
          </div>
        </div>

        {/* Right side: Fast plans stats */}
        <div className="bg-white p-6 rounded-2xl border border-slate-200/80 shadow-sm space-y-5">
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-indigo-600" />
            <h4 className="font-display font-bold text-lg text-slate-900">
              Tarif Dağılımı (Aktiv)
            </h4>
          </div>
          
          <div className="space-y-4">
            {['Free', 'Standard', 'Premium', 'Enterprise'].map((plan) => {
              const count = planDistribution[plan] || 0;
              const percentage = Math.round((count / (metrics.activeRest || 1)) * 100);
              
              const planTitles: Record<string, string> = {
                Free: 'Ödənişsiz Sınaq',
                Standard: 'Standart Tarif',
                Premium: 'Premium Metriklər',
                Enterprise: 'Korporativ Plan'
              };

              const planColors: Record<string, string> = {
                Free: 'bg-slate-400',
                Standard: 'bg-emerald-500',
                Premium: 'bg-indigo-600',
                Enterprise: 'bg-purple-600'
              };

              return (
                <div key={plan} className="space-y-1.5">
                  <div className="flex justify-between items-center text-xs font-semibold text-slate-700">
                    <span className="flex items-center gap-2">
                      <span className={`w-2.5 h-2.5 rounded-full ${planColors[plan] || 'bg-slate-300'}`} />
                      {planTitles[plan] || plan}
                    </span>
                    <span className="font-mono text-slate-500">{count} restoran ({percentage}%)</span>
                  </div>
                  <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${planColors[plan]}`} 
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="bg-emerald-50/50 p-4 rounded-xl border border-emerald-100/60 mt-2">
            <p className="text-[11px] text-emerald-800 leading-relaxed font-medium">
              💡 <strong>Hədəf Rəqəmlər:</strong> Korporativ (Enterprise) və Premium abunəçilər hazırkı aktiv bazanın <strong>65%-ni</strong> təşkil edir. Bu da platformanın MRR sağlamlıq dərəcəsini yüksək səviyyədə saxlayır.
            </p>
          </div>
        </div>
      </div>

      {/* Useful Leaderboard: Top performing restaurants by scans & Churn Risk in Azeri */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Deep metrics details - Top restaurants list */}
        <div className="bg-white p-6 rounded-2xl border border-slate-200/80 shadow-sm lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Flame className="w-5 h-5 text-amber-500" />
              <h4 className="font-display font-bold text-lg text-slate-900">
                Ən Aktiv Restoranlar (Skan üzrə Top 4)
              </h4>
            </div>
            <button 
              onClick={() => setActiveTab('restaurants')}
              className="text-xs text-indigo-600 font-bold hover:underline flex items-center gap-1"
            >
              <span>Hamısına bax</span>
              <ArrowUpRight className="w-3 h-3" />
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-slate-100 text-[11px] font-mono text-slate-400 uppercase tracking-wider pb-2">
                  <th className="font-semibold py-2">Restoran Adı</th>
                  <th className="font-semibold py-2">Mətbəx</th>
                  <th className="font-semibold py-2 text-center">Reytinq</th>
                  <th className="font-semibold py-2 text-right">Skan Sayı</th>
                  <th className="font-semibold py-2 text-right">Aylıq Dövriyyə ({'₼'})</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs">
                {topRestaurants.map((rest, index) => (
                  <tr key={rest.id} className="hover:bg-slate-50/60 transition-colors">
                    <td className="py-3 font-semibold text-slate-800">
                      <div className="flex items-center gap-2">
                        <span className="w-5 h-5 rounded bg-indigo-50 text-indigo-600 flex items-center justify-center font-mono text-[10px] font-bold">
                          #{index + 1}
                        </span>
                        <span>{rest.name}</span>
                      </div>
                    </td>
                    <td className="py-3 text-slate-500">{rest.cuisine}</td>
                    <td className="py-3 text-center">
                      <span className="inline-flex items-center gap-1 font-semibold text-amber-600 bg-amber-50 px-2 py-0.5 rounded font-mono text-[10px]">
                        <Star className="w-2.5 h-2.5 fill-amber-500 text-amber-500" />
                        {rest.rating}
                      </span>
                    </td>
                    <td className="py-3 text-right font-mono font-bold text-slate-700">
                      {(rest.scansThisMonth ?? 0).toLocaleString()}
                    </td>
                    <td className="py-3 text-right font-mono text-emerald-600 font-bold">
                      {(rest.revenueThisMonth ?? 0) > 0 ? `${(rest.revenueThisMonth ?? 0).toLocaleString()} ₼` : '0 ₼'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Churn analysis and quick helpers */}
        <div className="bg-slate-900 text-white p-6 rounded-2xl shadow-xl space-y-4 relative overflow-hidden">
          {/* Decorative gradients */}
          <div className="absolute right-0 top-0 w-32 h-32 bg-indigo-500/15 rounded-full blur-2xl flex-none pointer-events-none" />
          <div className="absolute left-1/3 bottom-0 w-24 h-24 bg-purple-500/15 rounded-full blur-xl flex-none pointer-events-none" />

          <div className="flex justify-between items-start">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-amber-400" />
              <h4 className="font-display font-extrabold text-base text-white tracking-tight">
                Müştəri İtkisi Riski (Churn)
              </h4>
            </div>
            <span className="bg-amber-500/20 text-amber-300 text-[9px] uppercase font-mono px-1.5 py-0.5 rounded font-bold border border-amber-500/30 animate-pulse">
              Diqqət
            </span>
          </div>
          
          <p className="text-slate-400 text-xs leading-relaxed">
            Sınaq müddəti (Trial) yaxın vaxtda bitən və ya aktiv dəstək müraciəti olan restoranlar:
          </p>

          <div className="space-y-2.5">
            {trialRestaurants.length === 0 ? (
              <p className="text-xs text-slate-400 font-mono py-2 italic text-center">Ödənişsiz sınaq olan müştəri tapılmadı.</p>
            ) : (
              trialRestaurants.map((rest) => (
                <div key={rest.id} className="bg-slate-800/80 p-3 rounded-xl border border-slate-700/60 hover:border-slate-600 transition-colors flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-slate-100 truncate">{rest.name}</p>
                    <p className="text-[10px] text-slate-400 font-mono">Sahib: {rest.ownerName}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <span className="block text-[10px] font-mono font-bold text-amber-400">
                      {rest.expiryDate}
                    </span>
                    <span className="text-[9px] text-slate-400 block font-mono">Sona çatır</span>
                  </div>
                </div>
              ))
            )}
          </div>

          <button 
            onClick={() => setActiveTab('ai-copilot')}
            className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-500 rounded-lg text-xs font-bold font-sans tracking-wide transition-all text-white flex items-center justify-center gap-1.5 shadow-lg shadow-indigo-600/20"
          >
            <Sparkles className="w-3.5 h-3.5" />
            <span>AI ilə qorunma bəyannaməsi al</span>
          </button>
        </div>

      </div>

      {/* Action Prompt */}
      <div className="bg-slate-100 p-6 rounded-2xl flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border border-slate-200">
        <div>
          <h4 className="font-bold text-slate-800 text-sm">Problemi olan müştərilərə AI ilə dəstək verin</h4>
          <p className="text-xs text-slate-500">Müştəri sorğularına saniyələr içində tam Azərbaycan dilində cavab layihəsi tərtib edin.</p>
        </div>
        <button 
          onClick={() => setActiveTab('support')}
          className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold shadow-sm transition-all flex items-center gap-1.5 self-start sm:self-auto shrink-0 select-none cursor-pointer"
        >
          <span>Dəstək biletlərini həll et</span>
          <ExternalLink className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};
